import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { z } from 'zod';
import { FormattedMessage } from 'react-intl';
import {
  PermittedIPList,
  IpAddressInputWrapper,
  StyledTextInput,
  StyledButton,
  ErrorMessage,
} from './styles/AllowedIPAddressesStyles';

const AllowedIPAddresses = ({
  deviceAuthSettings,
  addedIpAddress,
  removedIpAddress,
}) => {
  const initialIpAddressList = useMemo(() => {
    const baseIpAddressList =
      deviceAuthSettings?.ipAddresses?.map(ip => ({
        value: ip,
        disabled: true,
        error: false,
      })) || [];

    return [
      ...baseIpAddressList,
      { value: null, disabled: false, error: false },
    ];
  }, [deviceAuthSettings?.ipAddresses]);

  const [ipAddressFields, setIpAddressFields] = useState(initialIpAddressList);

  useEffect(() => {
    setIpAddressFields(initialIpAddressList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceAuthSettings]);

  // IPv4アドレスのバリデーションルールを定義
  const ipv4Schema = z
    .string()
    .regex(
      /^((25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])$/,
      '無効なIPv4アドレス',
    );

  const handleRemove = index => {
    setIpAddressFields(ipAddressFields.filter((field, i) => i !== index));
    removedIpAddress(index);
  };

  const handleAdd = () => {
    const lastField = ipAddressFields[ipAddressFields.length - 1];
    const result = ipv4Schema.safeParse(lastField.value);

    if (result.success) {
      const updatedFields = ipAddressFields.map(field => ({
        ...field,
        disabled: true,
      }));
      updatedFields.push({ value: null, disabled: false });
      setIpAddressFields(updatedFields);
      addedIpAddress(lastField.value);
    } else {
      const updatedFields = ipAddressFields.map((field, index) => {
        if (index === ipAddressFields.length - 1) {
          return { ...field, error: true };
        }
        return field;
      });
      setIpAddressFields(updatedFields);
    }
  };

  const handleChange = (index, event) => {
    const newFields = [...ipAddressFields];
    const newFieldValue = event.target.value;
    newFields[index].value = newFieldValue;
    setIpAddressFields(newFields);
  };

  const handleFocusOut = (index, event) => {
    const newFields = [...ipAddressFields];
    const newFieldValue = event.target.value;

    try {
      ipv4Schema.parse(newFieldValue);
      newFields[index] = {
        ...newFields[index],
        value: newFieldValue,
        error: false,
      };
    } catch (error) {
      // バリデーションエラー時の処理
      newFields[index] = { ...newFields[index], error: true };
    }
    setIpAddressFields(newFields);
  };

  return (
    <PermittedIPList>
      {ipAddressFields.map((field, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <IpAddressInputWrapper key={index}>
          <StyledTextInput
            type="text"
            disabled={field.disabled}
            value={field.value || ''}
            onChange={event => handleChange(index, event)}
            onBlur={event => handleFocusOut(index, event)}
            error={field.error}
            placeholder="IPアドレスを追加"
          />
          {!field.disabled && (
            <StyledButton type="button" onClick={handleAdd}>
              <FormattedMessage id="common.button.add" />
            </StyledButton>
          )}
          {field.disabled && (
            <StyledButton type="button" onClick={() => handleRemove(index)}>
              <FormattedMessage id="common.button.delete" />
            </StyledButton>
          )}
        </IpAddressInputWrapper>
      ))}
      {ipAddressFields.some(field => field.error) && (
        <ErrorMessage>
          <FormattedMessage id="accessControlPage.ipAddress.validationError" />
        </ErrorMessage>
      )}
    </PermittedIPList>
  );
};

AllowedIPAddresses.propTypes = {
  deviceAuthSettings: PropTypes.object.isRequired,
  addedIpAddress: PropTypes.func.isRequired,
  removedIpAddress: PropTypes.func.isRequired,
};

export default AllowedIPAddresses;
